import {
	DEFAULT_USDC_DECIMALS,
	V3_SYNTH_MARKET_KEYS,
	V3SynthMarketKey,
} from '@kwenta/sdk/constants'
import { formatDollars, formatNumber, stripZeros, truncateNumbers } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'

import BaseIcon from 'assets/svg/providers/base.svg'
import { TransactionButton } from 'components/Button/Button'
import ErrorView from 'components/ErrorView'
import InputWithAssetSelector from 'components/Input/InputWithAssetSelector'
import { FlexDivRowCentered } from 'components/layout/flex'
import {
	DepositableAssetKeysV3,
	DepositableTokenKeysV3,
	V3_DEPOSITABLE_ASSETS,
	V3_DEPOSITABLE_TOKENS,
} from 'constants/perpsV3'
import { setOpenModalWithParams } from 'state/app/reducer'
import { selectShowModalWithParams } from 'state/app/selectors'
import { selectV3DepositableBalances } from 'state/balances/selectors'
import {
	selectIsApprovingCrossDeposit,
	selectIsSubmittingIsolatedTransfer,
} from 'state/futures/selectors'
import {
	approveCrossMarginDeposit,
	depositCrossMarginMargin,
	depositWrappedTokenCrossMargin,
} from 'state/futures/snxPerpsV3/actions'
import {
	selectDepositAllowances,
	selectMarginEnginePermitted,
	selectUsdcMaxDepositAmount,
} from 'state/futures/snxPerpsV3/selectors'
import { ManageModalType } from 'state/futures/types'
import { useAppDispatch, useAppSelector } from 'state/hooks'

type Props = {
	closeOnComplete?: boolean
}

const DepositCrossMargin = memo(({ closeOnComplete }: Props) => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const allowances = useAppSelector(selectDepositAllowances)
	const v3Balances = useAppSelector(selectV3DepositableBalances)
	const submitting = useAppSelector(selectIsSubmittingIsolatedTransfer)
	const approving = useAppSelector(selectIsApprovingCrossDeposit)
	const marginEnginePermitted = useAppSelector(selectMarginEnginePermitted)
	const modalParams = useAppSelector(selectShowModalWithParams)?.params
	const maxDeposit = useAppSelector(selectUsdcMaxDepositAmount)
	const [selectedAsset, setSelectedAsset] = useState<DepositableAssetKeysV3>('USDC')
	const [amount, setAmount] = useState('')

	useEffect(() => {
		const asset = modalParams?.asset
		if (V3_SYNTH_MARKET_KEYS.includes(asset)) {
			setSelectedAsset(asset)
		}
	}, [modalParams?.asset])

	const minDeposit = useMemo(() => {
		// TODO: Update min deposit amount for cross margin
		return wei(0)
	}, [])

	const isWrappedDeposit = useMemo(
		() => V3_DEPOSITABLE_TOKENS[selectedAsset as DepositableTokenKeysV3],
		[selectedAsset]
	)

	const requiresApproval = useMemo(() => {
		const spender = marginEnginePermitted ? 'marginEngine' : 'marketProxy'
		return allowances[selectedAsset]?.[spender]?.lt(amount || 0)
	}, [allowances, amount, selectedAsset, marginEnginePermitted])

	const selectedAssetBalance = useMemo(
		() => v3Balances[selectedAsset] ?? wei(0),
		[selectedAsset, v3Balances]
	)

	const depositRequired = useMemo(() => {
		return selectedAssetBalance.lte(minDeposit)
	}, [minDeposit, selectedAssetBalance])

	const isOverMaxDeposit = useMemo(() => {
		if (!amount) return false
		return wei(amount).gt(maxDeposit)
	}, [amount, maxDeposit])

	const depositDisabled = useMemo(() => {
		if (!amount || submitting || approving) {
			return true
		}
		const amtWei = wei(amount)
		if (
			amtWei.eq(0) ||
			amtWei.gt(selectedAssetBalance) ||
			amtWei.lt(minDeposit) ||
			isOverMaxDeposit
		) {
			return true
		}
		return false
	}, [amount, submitting, approving, selectedAssetBalance, minDeposit, isOverMaxDeposit])

	const onSelectAsset = useCallback((asset: string) => {
		setAmount('')
		setSelectedAsset(asset as DepositableAssetKeysV3)
	}, [])

	const switchToBridge = useCallback(() => {
		dispatch(
			setOpenModalWithParams({
				type: 'futures_deposit_withdraw_cross_margin',
				params: { tab: ManageModalType.Bridge },
			})
		)
	}, [dispatch])

	const handleSetMax = useCallback(() => {
		setAmount(stripZeros(truncateNumbers(selectedAssetBalance, DEFAULT_USDC_DECIMALS)))
	}, [selectedAssetBalance])

	const onDepositOrApprove = useCallback(async () => {
		if (isWrappedDeposit) {
			dispatch(
				depositWrappedTokenCrossMargin({
					amount: wei(amount),
					token: selectedAsset as DepositableTokenKeysV3,
					closeOnComplete: true,
				})
			)
		} else {
			if (requiresApproval) {
				dispatch(approveCrossMarginDeposit({}))
			} else {
				dispatch(
					depositCrossMarginMargin({
						amount: wei(amount),
						synthKey: selectedAsset as V3SynthMarketKey,
						closeOnComplete,
					})
				)
			}
		}
	}, [requiresApproval, selectedAsset, amount, isWrappedDeposit, closeOnComplete, dispatch])

	return (
		<>
			<BalanceContainer onClick={handleSetMax}>
				<BalanceText>{t('futures.market.trade.margin.modal.balance')}:</BalanceText>
				<BalanceText>
					<span>{formatNumber(selectedAssetBalance, { suggestDecimals: true })}</span>{' '}
					{V3_DEPOSITABLE_ASSETS[selectedAsset]}
				</BalanceText>
			</BalanceContainer>
			<InputWithAssetSelector
				small={false}
				value={truncateNumbers(amount, DEFAULT_USDC_DECIMALS)}
				setValue={setAmount}
				selectedAsset={selectedAsset}
				onSelectAsset={onSelectAsset}
			/>
			<MinimumAmountDisclaimer>
				{t('futures.market.trade.margin.modal.deposit.disclaimer')}
			</MinimumAmountDisclaimer>

			{isOverMaxDeposit && (
				<ErrorView
					messageType="error"
					message={''}
					messageWithIcon={
						<Trans
							i18nKey="futures.market.trade.margin.modal.deposit.max-deposit"
							values={{ maxAmount: formatDollars(maxDeposit) }}
						/>
					}
				/>
			)}
			{depositRequired && (
				<ErrorView
					messageType="info"
					message={''}
					messageWithIcon={
						<Trans
							i18nKey="futures.market.trade.margin.modal.deposit.bridge-to-base"
							components={[<BaseIcon height={25} width={25} />, <b />]}
							values={{ dest: 'Base' }}
						/>
					}
					containerStyle={{
						cursor: 'pointer',
						display: 'flex',
						flexDirection: 'row',
						columnGap: '4px',
					}}
					onClick={switchToBridge}
				/>
			)}

			<TransactionButton
				data-testid="futures-market-trade-deposit-margin-button"
				disabled={depositDisabled}
				loading={submitting || approving}
				fullWidth
				onClick={onDepositOrApprove}
				variant="flat"
			>
				{requiresApproval
					? isWrappedDeposit
						? t('futures.market.trade.margin.modal.deposit.approve-and-deposit')
						: t('futures.market.trade.margin.modal.deposit.approve-button', {
								asset: V3_DEPOSITABLE_ASSETS[selectedAsset],
						  })
					: t('futures.market.trade.margin.modal.deposit.button')}
			</TransactionButton>
		</>
	)
})

export default DepositCrossMargin

export const BalanceContainer = styled(FlexDivRowCentered)`
	cursor: pointer;
	margin-bottom: 8px;
	p {
		margin: 0;
	}
`

export const BalanceText = styled.p<{ $gold?: boolean }>`
	color: ${(props) =>
		props.$gold ? props.theme.colors.selectedTheme.yellow : props.theme.colors.selectedTheme.gray};
	span {
		color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	}
`

const MinimumAmountDisclaimer = styled.div`
	font-size: 12px;
	margin: 14px 0;
	color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	text-align: center;
`
